import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Check, X, Star, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminYhListings } from "@/components/admin/YhListings";
import { AdminCmsHome } from "@/components/admin/CmsHome";

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [newCat, setNewCat] = useState({ name: "", icon: "Store", order: 100 });

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    reload();
  }, [user, loading, nav]);

  const reload = async () => {
    api.get("/admin/businesses").then(({ data }) => setBusinesses(data));
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/admin/reviews").then(({ data }) => setReviews(data));
  };

  const approve = async (bid, status) => {
    await api.post(`/admin/businesses/${bid}/approval`, { status });
    toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
    reload();
  };
  const toggleFeatured = async (bid) => { await api.post(`/admin/businesses/${bid}/toggle-featured`); reload(); };
  const toggleActive = async (bid) => { await api.post(`/admin/businesses/${bid}/toggle-active`); reload(); };
  const setPlan = async (bid, plan) => { await api.post(`/admin/businesses/${bid}/plan`, null, { params: { plan } }); reload(); };

  const addCat = async () => {
    if (!newCat.name) return;
    await api.post("/categories", newCat);
    setNewCat({ name: "", icon: "Store", order: 100 });
    reload();
  };
  const delCat = async (id) => { await api.delete(`/categories/${id}`); reload(); };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); reload(); };

  const pending = businesses.filter((b) => b.status === "pending");
  const others = businesses.filter((b) => b.status !== "pending");

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <h1 className="text-2xl font-bold tracking-tight mb-4">Admin Paneli</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-overview" value="overview">Genel Bakış</TabsTrigger>
          <TabsTrigger data-testid="tab-pending" value="pending">Onay Bekleyen ({pending.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-businesses" value="businesses">İşletmeler</TabsTrigger>
          <TabsTrigger data-testid="tab-users" value="users">Kullanıcılar</TabsTrigger>
          <TabsTrigger data-testid="tab-categories" value="categories">Kategoriler</TabsTrigger>
          <TabsTrigger data-testid="tab-reviews" value="reviews">Yorumlar</TabsTrigger>
          <TabsTrigger data-testid="tab-memberships" value="memberships">Üyelikler / Ödemeler</TabsTrigger>
          <TabsTrigger data-testid="tab-banks" value="banks">Banka Bilgileri</TabsTrigger>
          <TabsTrigger data-testid="tab-analytics" value="analytics">Firmalar / Ciro</TabsTrigger>
          <TabsTrigger data-testid="tab-yh-listings" value="yh-listings">Yol Hattı İş İlanları</TabsTrigger>
          <TabsTrigger data-testid="tab-cms-home" value="cms-home">Ana Sayfa Yönetimi</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <AdminOverview onNav={setTab} />
        </TabsContent>

        <TabsContent value="pending">
          {pending.length === 0 && <p className="text-muted-foreground">Onay bekleyen işletme yok.</p>}
          <div className="grid gap-3">
            {pending.map((b) => (
              <div key={b.id} className="rounded-2xl border border-border p-4">
                <h3 className="font-semibold">{b.name}</h3>
                <p className="text-xs text-muted-foreground">{b.district}, {b.province}</p>
                <p className="text-sm mt-2">{b.description}</p>
                <div className="flex gap-2 mt-3">
                  <Button data-testid={`approve-${b.id}`} size="sm" onClick={() => approve(b.id, "approved")} className="rounded-full"><Check className="w-4 h-4 mr-1" /> Onayla</Button>
                  <Button data-testid={`reject-${b.id}`} size="sm" variant="destructive" onClick={() => approve(b.id, "rejected")} className="rounded-full"><X className="w-4 h-4 mr-1" /> Reddet</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="businesses">
          <div className="grid gap-3">
            {others.map((b) => (
              <div key={b.id} className="rounded-2xl border border-border p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold truncate">{b.name}</h3>
                    <Badge variant={b.status === "approved" ? "default" : "destructive"}>{b.status}</Badge>
                    <Badge>{b.plan}</Badge>
                    {b.featured && <Badge className="bg-amber-500">Öne Çıkan</Badge>}
                    {!b.is_active && <Badge variant="secondary">Pasif</Badge>}
                    {b.is_demo && <Badge variant="secondary">DEMO</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{b.district}, {b.province}</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Select value={b.plan} onValueChange={(v) => setPlan(b.id, v)}>
                    <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Ücretsiz</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => toggleFeatured(b.id)} data-testid={`feat-${b.id}`}><Star className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(b.id)}><Power className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="rounded-2xl border border-border overflow-hidden">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-4 py-3 border-b last:border-b-0">
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </div>
                <Badge>{u.role}</Badge>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <div className="rounded-2xl border border-border p-4 mb-4">
            <h3 className="font-semibold mb-2">Yeni Kategori</h3>
            <div className="flex flex-wrap gap-2">
              <Input placeholder="Kategori adı" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="flex-1 min-w-40" />
              <Input placeholder="Icon (Lucide)" value={newCat.icon} onChange={(e) => setNewCat({ ...newCat, icon: e.target.value })} className="w-40" />
              <Button onClick={addCat} data-testid="add-cat">Ekle</Button>
            </div>
          </div>
          <div className="grid gap-2">
            {categories.map((c) => (
              <div key={c.id} className="rounded-xl border border-border px-4 py-2 flex items-center justify-between">
                <span className="font-medium">{c.name} <span className="text-xs text-muted-foreground">({c.slug})</span></span>
                <Button size="sm" variant="ghost" onClick={() => delCat(c.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="grid gap-2">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-xl border border-border p-3 flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm"><b>{r.user_name}</b> — <span className="text-amber-500">{"★".repeat(r.rating)}</span></div>
                  <div className="text-sm text-muted-foreground">{r.comment}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => delReview(r.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="memberships">
          <AdminMemberships />
        </TabsContent>

        <TabsContent value="banks">
          <AdminBanks />
        </TabsContent>

        <TabsContent value="analytics">
          <AdminAnalytics />
        </TabsContent>

        <TabsContent value="yh-listings">
          <AdminYhListings />
        </TabsContent>

        <TabsContent value="cms-home">
          <AdminCmsHome />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== ADMIN — MEMBERSHIPS / PAYMENTS ====================
function AdminMemberships() {
  const [rows, setRows] = useState([]);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ provider_id: "", amount: "", payment_method: "bank_transfer", note: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [m, p] = await Promise.all([
      api.get("/admin/memberships").then((r) => r.data).catch(() => []),
      api.get("/admin/payments").then((r) => r.data).catch(() => []),
    ]);
    setRows(m); setPayments(p); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addPayment = async () => {
    if (!form.provider_id || !form.amount) { toast.error("Firma ve tutar zorunludur"); return; }
    try {
      await api.post("/admin/payments", { ...form, amount: parseFloat(form.amount), status: "approved" });
      toast.success("Ödeme kaydedildi ve üyelik 365 gün uzatıldı");
      setForm({ provider_id: "", amount: "", payment_method: "bank_transfer", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-4" data-testid="admin-memberships">
      <MembershipFeeCard />

      <PendingPaymentsCard onChanged={load} />

      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-2">Manuel Ödeme Kaydı (Admin — 365 gün üyelik)</h3>
        <div className="grid gap-2 sm:grid-cols-4">
          <Select value={form.provider_id} onValueChange={(v) => setForm({ ...form, provider_id: v })}>
            <SelectTrigger data-testid="pay-provider"><SelectValue placeholder="Firma seç" /></SelectTrigger>
            <SelectContent>
              {rows.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.company_name || u.name} · {u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="number" step="1" placeholder="Tutar (₺)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
          <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
            <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bank_transfer">Banka Transferi</SelectItem>
              <SelectItem value="credit_card">Kredi Kartı</SelectItem>
              <SelectItem value="manual">Manuel/Admin</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Not / Referans" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pay-note" />
        </div>
        <Button className="mt-3" onClick={addPayment} data-testid="pay-submit">Ödemeyi Kaydet ve Üyeliği Aktive Et</Button>
      </div>

      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-2">Üyelik Listesi</h3>
        <div className="grid gap-2">
          {rows.map((u) => {
            const clr = u.membership.status === "active" ? "bg-[hsl(var(--success))] text-white" : u.membership.status === "expired" ? "bg-destructive text-white" : "bg-slate-500 text-white";
            const label = { active: "Aktif", expired: "Süresi Doldu", none: "Üye Değil" }[u.membership.status];
            return (
              <div key={u.id} className="rounded-xl border p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`mem-${u.id}`}>
                <div className="min-w-0 text-sm">
                  <div className="font-semibold">{u.company_name || u.name} <span className="text-muted-foreground font-normal">({u.name} {u.surname || ""})</span></div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.phone || "—"}</div>
                  <div className="text-xs mt-1">Bitiş: <b>{u.membership.expires_at ? new Date(u.membership.expires_at).toLocaleDateString("tr-TR") : "—"}</b> · Kalan: <b>{u.membership.remaining_days} gün</b></div>
                </div>
                <Badge className={clr}>{label}</Badge>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-2">Ödeme Geçmişi (Tüm Kayıtlar)</h3>
        {payments.length === 0 ? <div className="text-sm text-muted-foreground">Kayıt yok.</div> : (
          <div className="grid gap-2 text-sm">
            {payments.map((p) => (
              <div key={p.id} className="rounded-lg border p-2 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold text-sm">{p.provider_company_name || rows.find((r) => r.id === p.provider_id)?.company_name || p.provider_name || rows.find((r) => r.id === p.provider_id)?.name || p.provider_id}</div>
                  <div className="text-xs text-muted-foreground">{new Date(p.payment_date).toLocaleString("tr-TR")}</div>
                  <div><b>{Number(p.amount).toLocaleString("tr-TR")} ₺</b> · {p.payment_method}</div>
                  {p.note && <div className="text-xs italic">{p.note}</div>}
                </div>
                <Badge className={p.status === "approved" ? "bg-[hsl(var(--success))] text-white" : p.status === "rejected" ? "bg-destructive text-white" : "bg-amber-500 text-white"}>{p.status === "approved" ? "Onaylı" : p.status === "rejected" ? "Reddedildi" : "Bekliyor"}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== ADMIN — BANK ACCOUNTS ====================
function AdminBanks() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ bank_name: "", account_holder: "", iban: "", branch: "", note: "", is_active: true });
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/bank-accounts");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.bank_name || !form.account_holder || !form.iban) { toast.error("Banka adı, hesap sahibi ve IBAN zorunludur"); return; }
    try {
      if (editId) {
        await api.put(`/admin/bank-accounts/${editId}`, form);
        toast.success("Güncellendi");
      } else {
        await api.post("/admin/bank-accounts", form);
        toast.success("Eklendi");
      }
      setForm({ bank_name: "", account_holder: "", iban: "", branch: "", note: "", is_active: true });
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Başarısız");
    }
  };
  const edit = (r) => { setForm(r); setEditId(r.id); };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/admin/bank-accounts/${id}`); load(); };

  return (
    <div className="space-y-3" data-testid="admin-banks">
      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-2">{editId ? "Banka Hesabı Düzenle" : "Yeni Banka Hesabı"}</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input placeholder="Banka Adı" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-name" />
          <Input placeholder="Hesap Sahibi" value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} data-testid="bank-holder" />
          <Input placeholder="IBAN" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="bank-iban" />
          <Input placeholder="Şube/Hesap No (ops.)" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} data-testid="bank-branch" />
          <Input placeholder="Açıklama" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="bank-note" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="bank-active" /> Aktif</label>
        </div>
        <div className="mt-2 flex gap-2">
          <Button onClick={submit} data-testid="bank-submit">{editId ? "Güncelle" : "Ekle"}</Button>
          {editId && <Button variant="ghost" onClick={() => { setEditId(null); setForm({ bank_name: "", account_holder: "", iban: "", branch: "", note: "", is_active: true }); }}>Vazgeç</Button>}
        </div>
      </div>
      <div className="grid gap-2">
        {rows.length === 0 && <div className="text-sm text-muted-foreground">Henüz kayıt yok.</div>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`bank-row-${r.id}`}>
            <div className="min-w-0 text-sm">
              <div><b>{r.bank_name}</b> — {r.account_holder}</div>
              <div className="font-mono text-xs">IBAN: {r.iban}</div>
              {r.branch && <div className="text-xs">Şube: {r.branch}</div>}
              {r.note && <div className="text-xs italic">{r.note}</div>}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={r.is_active ? "bg-[hsl(var(--success))] text-white" : "bg-slate-500 text-white"}>{r.is_active ? "Aktif" : "Pasif"}</Badge>
              <Button size="sm" variant="ghost" onClick={() => edit(r)}>Düzenle</Button>
              <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== ADMIN — PROVIDERS ANALYTICS ====================
function AdminAnalytics() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get("/admin/providers/analytics").then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  const openDetail = async (pid) => {
    try {
      const { data } = await api.get(`/admin/providers/${pid}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error("Detay yüklenemedi");
    }
  };
  if (loading) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;
  return (
    <div className="grid gap-3" data-testid="admin-analytics">
      {rows.length === 0 && <div className="text-sm text-muted-foreground">Firma yok.</div>}
      {rows.map((r) => (
        <button
          key={r.provider_id}
          type="button"
          onClick={() => openDetail(r.provider_id)}
          className="text-left rounded-2xl border p-4 hover:bg-muted/40 transition"
          data-testid={`ana-${r.provider_id}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0">
              <div className="font-semibold">{r.company_name || r.name} <span className="text-muted-foreground text-sm font-normal">({r.name} {r.surname || ""})</span></div>
              <div className="text-xs text-muted-foreground">{r.email} · {r.phone || "—"}</div>
              {r.address && <div className="text-xs text-muted-foreground">{r.address}</div>}
              <div className="text-xs mt-1">Araç: <b>{r.vehicle_count}</b> · Şoför: <b>{r.driver_count}</b> · Firma: <b>{r.businesses.length}</b></div>
            </div>
            <Badge className={r.membership.status === "active" ? "bg-[hsl(var(--success))] text-white" : "bg-slate-500 text-white"}>
              Üyelik: {r.membership.status === "active" ? `Aktif (${r.membership.remaining_days} gün)` : r.membership.status === "expired" ? "Süresi Doldu" : "Üye Değil"}
            </Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {["today", "month", "year", "total"].map((k) => (
              <div key={k} className="rounded-xl bg-muted p-2 text-center">
                <div className="text-xs text-muted-foreground uppercase">{ {today: "Bugün", month: "Bu Ay", year: "Bu Yıl", total: "Toplam"}[k] }</div>
                <div className="text-sm font-bold">{r.analytics[k].job_count} iş</div>
                <div className="text-xs text-primary font-semibold">{Number(r.analytics[k].revenue).toLocaleString("tr-TR")} ₺</div>
              </div>
            ))}
          </div>
          <div className="text-right text-xs text-primary mt-2">Detayı gör →</div>
        </button>
      ))}
      {detail && <ProviderDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ProviderDetailModal({ detail, onClose }) {
  const u = detail.user || {};
  const m = detail.membership || {};
  const a = detail.analytics || {};
  const methodLabel = (mm) => ({ bank_transfer: "Banka Transferi", credit_card: "Kredi Kartı", manual: "Manuel/Admin" }[mm] || mm);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-auto" onClick={onClose} data-testid="provider-detail-modal">
      <div className="bg-background text-foreground w-full max-w-3xl rounded-2xl border shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold" data-testid="provider-detail-title">{u.company_name || u.name} <span className="text-muted-foreground text-base font-normal">— Firma Detayı</span></h2>
          <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl border p-3">
            <div className="font-semibold mb-1">İletişim</div>
            <div>Yetkili: <b>{u.name} {u.surname || ""}</b></div>
            <div>Telefon: <b>{u.phone || "—"}</b></div>
            <div>E-posta: <b>{u.email}</b></div>
            {u.address && <div>Adres: {u.address}</div>}
            <div className="text-xs text-muted-foreground mt-1">Kayıt: {u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "—"}</div>
          </div>
          <div className="rounded-xl border p-3">
            <div className="font-semibold mb-1">Üyelik</div>
            <div>Durum: <Badge className={m.status === "active" ? "bg-[hsl(var(--success))] text-white" : m.status === "expired" ? "bg-destructive text-white" : "bg-slate-500 text-white"}>{{active:"Aktif", expired:"Süresi Doldu", none:"Üye Değil"}[m.status]}</Badge></div>
            <div>Başlangıç: {m.started_at ? new Date(m.started_at).toLocaleDateString("tr-TR") : "—"}</div>
            <div>Bitiş: {m.expires_at ? new Date(m.expires_at).toLocaleDateString("tr-TR") : "—"}</div>
            <div>Kalan Gün: <b>{m.remaining_days ?? 0}</b></div>
          </div>
        </div>

        <div className="rounded-xl border p-3 mt-3">
          <div className="font-semibold mb-1">İşletmeler ({(detail.businesses || []).length})</div>
          <div className="grid gap-1 text-sm">
            {(detail.businesses || []).map((b) => (
              <div key={b.id} className="text-xs">
                <b>{b.name}</b> · {b.province}/{b.district} {b.address ? `· ${b.address}` : ""}
                {b.category_ids && b.category_ids.length > 0 && <span className="text-muted-foreground"> · {b.category_ids.length} hizmet alanı</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {["today","month","year","total"].map((k) => (
            <div key={k} className="rounded-xl bg-muted p-2 text-center">
              <div className="text-xs text-muted-foreground uppercase">{ {today:"Bugün", month:"Bu Ay", year:"Bu Yıl", total:"Toplam"}[k] }</div>
              <div className="text-sm font-bold">{a[k]?.job_count ?? 0} iş</div>
              <div className="text-xs text-primary font-semibold">{Number(a[k]?.revenue ?? 0).toLocaleString("tr-TR")} ₺</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border p-3 mt-3" data-testid="detail-payments">
          <div className="font-semibold mb-1">Ödeme Geçmişi ({(detail.payments || []).length})</div>
          {(detail.payments || []).length === 0 ? <div className="text-sm text-muted-foreground">Kayıt yok.</div> : (
            <div className="grid gap-1 text-sm">
              {(detail.payments || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs">
                  <div>{new Date(p.payment_date).toLocaleDateString("tr-TR")} — <b>{Number(p.amount).toLocaleString("tr-TR")} ₺</b> · {methodLabel(p.payment_method)}{p.reference ? ` · ${p.reference}` : ""}</div>
                  <Badge className={p.status === "approved" ? "bg-[hsl(var(--success))] text-white" : p.status === "pending" ? "bg-amber-500 text-white" : "bg-destructive text-white"}>{{approved:"Onaylı", pending:"Bekliyor", rejected:"Reddedildi"}[p.status] || p.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MembershipFeeCard() {
  const [fee, setFee] = useState(null);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get("/admin/settings/membership-fee");
    setFee(data.amount);
    if (data.amount) setVal(String(data.amount));
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    const amount = parseFloat(val);
    if (!isFinite(amount) || amount <= 0) { toast.error("Geçerli bir tutar giriniz"); return; }
    setSaving(true);
    try {
      await api.put("/admin/settings/membership-fee", { amount, currency: "TRY" });
      toast.success("Üyelik ücreti güncellendi");
      load();
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border p-4" data-testid="fee-card">
      <h3 className="font-semibold mb-2">365 Günlük Yol Hattı Üyelik Ücreti</h3>
      <div className="text-sm text-muted-foreground mb-2">Bu tutar tüm sağlayıcı paneline "sabit fiyat" olarak yansır. Değişiklik geçmiş ödemeleri etkilemez, yalnızca sonraki bildirimleri etkiler.</div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <div className="text-xs text-muted-foreground mb-1">Tutar (₺)</div>
          <Input type="number" min="0" step="1" value={val} onChange={(e) => setVal(e.target.value)} data-testid="fee-input" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="fee-save">{saving ? "..." : (fee ? "Güncelle" : "Belirle")}</Button>
      </div>
      {fee != null && <div className="text-sm mt-2">Mevcut: <b data-testid="fee-current">{Number(fee).toLocaleString("tr-TR")} ₺</b></div>}
    </div>
  );
}

function PendingPaymentsCard({ onChanged }) {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState({});
  const load = async () => {
    const { data } = await api.get("/admin/payments");
    setItems(data.filter((p) => p.status === "pending"));
    // Load bank names
    try {
      const bs = await api.get("/admin/bank-accounts");
      const bm = {}; bs.data.forEach((b) => { bm[b.id] = b.bank_name; });
      setBanks(bm);
    } catch {/* noop */}
  };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try { await api.post(`/admin/payments/${id}/approve`); toast.success("Onaylandı; üyelik 365 gün uzatıldı"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Onaylanamadı"); }
  };
  const reject = async (id) => {
    if (!window.confirm("Bu ödeme bildirimini reddedecek misiniz?")) return;
    try { await api.post(`/admin/payments/${id}/reject`); toast.success("Reddedildi"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reddedilemedi"); }
  };
  const receipt = async (id) => {
    // Etap 14: authenticated fetch → blob → open in new tab. Browsers don't send
    // cookies to programmatic window.open cross-cases, so we must use the axios
    // instance (which carries the JWT via cookie AND legacy Authorization if any).
    try {
      const res = await api.get(`/payments/${id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      // Free after 1 min.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Bu dekonta erişim yetkiniz yok" : "Dekont açılamadı");
    }
  };
  return (
    <div className="rounded-2xl border p-4" data-testid="pending-payments">
      <h3 className="font-semibold mb-2">Onay Bekleyen Sağlayıcı Ödeme Bildirimleri ({items.length})</h3>
      {items.length === 0 ? <div className="text-sm text-muted-foreground">Onay bekleyen ödeme yok.</div> : (
        <div className="grid gap-2 text-sm">
          {items.map((p) => (
            <div key={p.id} className="rounded-lg border p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`pending-pay-${p.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-sm" data-testid={`pending-pay-provider-${p.id}`}>
                  {p.provider_company_name || p.provider_name || "—"}
                  <span className="text-muted-foreground font-normal ml-1">({[p.provider_name, p.provider_surname].filter(Boolean).join(" ") || "—"})</span>
                </div>
                <div className="text-xs text-muted-foreground">{p.provider_email} · {p.provider_phone || "—"}</div>
                <div className="text-xs text-muted-foreground mt-1">{new Date(p.payment_date).toLocaleString("tr-TR")}</div>
                <div><b>{Number(p.amount).toLocaleString("tr-TR")} ₺</b> · {banks[p.bank_account_id] || p.bank_name_snapshot || "—"} · Ref: {p.reference || "—"}</div>
                {p.note && <div className="text-xs italic">{p.note}</div>}
              </div>
              <div className="flex items-center gap-2">
                {p.receipt_path && <Button size="sm" variant="outline" onClick={() => receipt(p.id)} data-testid={`pay-receipt-${p.id}`}>Dekont</Button>}
                <Button size="sm" onClick={() => approve(p.id)} data-testid={`pay-approve-${p.id}`}>Onayla</Button>
                <Button size="sm" variant="ghost" onClick={() => reject(p.id)} data-testid={`pay-reject-${p.id}`}>Reddet</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminOverview({ onNav }) {
  const [stats, setStats] = useState(null);
  const [drill, setDrill] = useState(null); // {status:'active_membership'|'unpaid'|'expired', rows:[]}
  useEffect(() => {
    api.get("/admin/dashboard/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);
  const open = async (status, label) => {
    try {
      const { data } = await api.get("/admin/providers", { params: { status } });
      setDrill({ status, label, rows: data });
    } catch { toast.error("Yüklenemedi"); }
  };
  if (!stats) return <div className="text-sm text-muted-foreground" data-testid="admin-overview">Yükleniyor…</div>;
  const kpi = (label, value, testid, onClick) => (
    <button
      type="button"
      className={`rounded-2xl border p-4 text-left ${onClick ? "hover:bg-muted/40 transition" : "cursor-default"}`}
      onClick={onClick}
      data-testid={testid}
      disabled={!onClick}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </button>
  );
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div>
        <h3 className="font-semibold mb-2">Kullanıcı İstatistikleri</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {kpi("TOPLAM KULLANICI", stats.users.total, "kpi-users-total")}
          {kpi("AKTİF KULLANICI", stats.users.active, "kpi-users-active")}
          {kpi("PASİF KULLANICI", stats.users.passive, "kpi-users-passive")}
        </div>
      </div>
      <div>
        <h3 className="font-semibold mb-2">Taşıyıcı / Hizmet Sağlayıcı</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {kpi("TOPLAM TAŞIYICI", stats.providers.total, "kpi-prov-total")}
          {kpi("AKTİF ÜYELİK", stats.providers.active_membership, "kpi-prov-active", () => open("active_membership", "Aktif Üyelikli Taşıyıcılar"))}
          {kpi("ÖDEME YAPMAMIŞ", stats.providers.unpaid, "kpi-prov-unpaid", () => open("unpaid", "Ödeme Yapmamış Taşıyıcılar"))}
          {kpi("SÜRESİ DOLMUŞ", stats.providers.expired, "kpi-prov-expired", () => open("expired", "Süresi Dolmuş Üyelikler"))}
          {kpi("ONAY BEKLEYEN ÖDEME", stats.pending_payments, "kpi-pay-pending", () => onNav && onNav("memberships"))}
        </div>
      </div>
      {drill && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-auto" onClick={() => setDrill(null)}>
          <div className="bg-background text-foreground w-full max-w-2xl rounded-2xl border shadow-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="kpi-drill-modal">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold">{drill.label} ({drill.rows.length})</h2>
              <Button size="sm" variant="ghost" onClick={() => setDrill(null)}><X className="w-4 h-4" /></Button>
            </div>
            {drill.rows.length === 0 ? <div className="text-sm text-muted-foreground">Kayıt yok.</div> : (
              <div className="grid gap-1">
                {drill.rows.map((u) => (
                  <div key={u.id} className="text-sm rounded-lg border p-2" data-testid={`kpi-drill-${u.id}`}>
                    <div className="font-semibold">{u.company_name || u.name} <span className="text-muted-foreground font-normal">({u.name} {u.surname || ""})</span></div>
                    <div className="text-xs text-muted-foreground">{u.email} · {u.phone || "—"} · Bitiş: {u.membership?.expires_at ? new Date(u.membership.expires_at).toLocaleDateString("tr-TR") : "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

